import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { catalogTenantsService } from "@/services/catalog-tenants-service";
import {
  CreateTenantDto,
  RegisterTenantWithAdminDto,
  TenantFilter,
  UpdateTenantDto,
  UpdateTenantPaymentsDto,
} from "@/types/tenant-catalog";

export const tenantCatalogKeys = {
  all: ["tenantCatalog"] as const,
  lists: () => [...tenantCatalogKeys.all, "list"] as const,
  list: (filters?: TenantFilter) =>
    [...tenantCatalogKeys.lists(), filters] as const,
  details: () => [...tenantCatalogKeys.all, "detail"] as const,
  detail: (tenantId: string) =>
    [...tenantCatalogKeys.details(), tenantId] as const,
};

export const useTenants = (query: TenantFilter, enabled: boolean) => {
  return useQuery({
    queryKey: tenantCatalogKeys.list(query),
    queryFn: () => catalogTenantsService.getTenants(query),
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useTenant = (tenantId: string, enabled: boolean) => {
  return useQuery({
    queryKey: tenantCatalogKeys.detail(tenantId),
    queryFn: () => catalogTenantsService.getTenantById(tenantId),
    staleTime: 60 * 1000,
    enabled: enabled && !!tenantId,
  });
};

export const useCreateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateTenantDto) =>
      catalogTenantsService.createTenant(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.lists() });
    },
  });
};

export const useRegisterTenantWithAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: RegisterTenantWithAdminDto) =>
      catalogTenantsService.registerTenant(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.lists() });
    },
  });
};

export const useUpdateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      dto,
    }: {
      tenantId: string;
      dto: UpdateTenantDto;
    }) => catalogTenantsService.patchTenant(tenantId, dto),
    onSuccess: (tenant) => {
      queryClient.setQueryData(tenantCatalogKeys.detail(tenant.id), tenant);
      queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.lists() });
    },
  });
};

export const useUpdateTenantPayments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      dto,
    }: {
      tenantId: string;
      dto: UpdateTenantPaymentsDto;
    }) => catalogTenantsService.patchTenantPayments(tenantId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.details() });
      queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.lists() });
    },
  });
};
